// #region: Class definition *********************************************************************/
/**
 * @class
 * Manages localization for web components.
 * Provides a centralized way to register, retrieve, and manage locale data.
 */
window.localeManager = new class {

    // #region: Private Fields ***********************************************************************/
    #locales = new Map();
    #defaultLocale = 'nl-NL';
    #currentLocale = null;
    // #endregion ************************************************************************************/

    // #region: Public Methods ***********************************************************************/
    /**
     * Formats a string with placeholders.
     * @param {string} template The template string with placeholders (e.g., 'Hello {name}').
     * @param {Object} values The values to replace placeholders with.
     * @returns {string} The formatted string.
     */
    format(template, values) {
        return template.replace(/{(\w+)}/g, (match, key) => {
            return values[key] === undefined ? match : values[key];
        });
    }

    /**
     * Gets locale data for a specific locale and namespace.
     * @param {string} locale The locale identifier.
     * @param {string} namespace The namespace for the locale data.
     * @returns {Object|null} The locale data or null if not found.
     */
    get(locale, namespace) {
        const lowercaseLocale = locale.toLowerCase();

        // Try exact locale match.
        if (this.#locales.has(lowercaseLocale)) {
            const localeData = this.#locales.get(lowercaseLocale);

            if (localeData.has(namespace)) {
                return localeData.get(namespace);
            }
        }

        // Try language-only match (e.g., 'en' from 'en-GB').
        if (lowercaseLocale.includes('-')) {
            const language = lowercaseLocale.split('-')[0];

            for (const [key, value] of this.#locales) {
                if (key.startsWith(language + '-')) {
                    if (value.has(namespace)) {
                        return value.get(namespace);
                    }
                }
            }
        }

        // Try default locale.
        if (this.#locales.has(this.#defaultLocale)) {
            const defaultData = this.#locales.get(this.#defaultLocale);

            if (defaultData.has(namespace)) {
                return defaultData.get(namespace);
            }
        }

        // If requested locale not found, use first available locale and log warning.
        if (this.#locales.size > 0) {
            const firstAvailableLocale = this.#locales.keys().next().value;
            const firstLocaleData = this.#locales.get(firstAvailableLocale);
            
            if (firstLocaleData.has(namespace)) {
                console.warn(`Locale '${locale}' not found for namespace '${namespace}'. Using '${firstAvailableLocale}' instead.`);

                return firstLocaleData.get(namespace);
            }
        }

        return null;
    }

    /**
     * Gets the current global locale.
     * @returns {string} The current locale identifier.
     */
    getCurrentLocale() {
        if (this.#currentLocale === null) {
            const htmlLang = this.#getHtmlLangAttribute();

            if (htmlLang) {
                this.#currentLocale = htmlLang.toLowerCase();
            }
            else {
                this.#currentLocale = this.#defaultLocale;
            }
        }

        return this.#currentLocale;
    }

    /**
     * Gets the default locale.
     * @returns {string} The default locale identifier.
     */
    getDefaultLocale() {
        return this.#defaultLocale;
    }

    /**
     * Gets all namespaces for a specific locale.
     * @param {string} locale The locale identifier.
     * @returns {string[]} Array of namespace names.
     */
    getNamespaces(locale) {
        const lowercaseLocale = locale.toLowerCase();

        if (this.#locales.has(lowercaseLocale)) {
            return Array.from(this.#locales.get(lowercaseLocale).keys());
        }

        return [];
    }

    /**
     * Gets all registered locales.
     * @returns {string[]} Array of locale identifiers.
     */
    getRegisteredLocales() {
        return Array.from(this.#locales.keys());
    }
   
    /**
     * Gets a nested value from an object using a dot-notation path.
     * @param {Object} obj The object to search.
     * @param {string} path The dot-notation path (e.g., 'buttons.save').
     * @param {*} defaultValue The default value if path not found.
     * @returns {*} The value at the path or the default value.
     */
    getValue(obj, path, defaultValue = '') {
        return path.split('.').reduce((current, key) => {
            return current?.[key] ?? defaultValue;
        }, obj);
    }

    /**
     * Merges multiple locale objects into one.
     * @param {...Object} objects The objects to merge.
     * @returns {Object} The merged object.
     */
    merge(...objects) {
        return Object.assign({}, ...objects);
    }

    /**
     * Registers locale data for a specific locale and namespace.
     * @param {string} locale The locale identifier (e.g., 'en-US', 'nl-NL').
     * @param {string} namespace The namespace for the locale data (e.g., 'datetime', 'common').
     * @param {Object} data The locale data object.
     */
    register(locale, namespace, data) {
        const lowercaseLocale = locale.toLowerCase();

        if (!this.#locales.has(lowercaseLocale)) {
            this.#locales.set(lowercaseLocale, new Map());
        }

        const localeData = this.#locales.get(lowercaseLocale);

        // Merge with existing data if namespace already exists.
        if (localeData.has(namespace)) {
            const existingData = localeData.get(namespace);

            localeData.set(namespace, { ...existingData, ...data });
        }
        else {
            localeData.set(namespace, data);
        }
    }

    /**
     * Sets the current global locale.
     * @param {string} locale The locale identifier.
     */
    setCurrentLocale(locale) {
        const lowercaseLocale = locale.toLowerCase();

        this.#currentLocale = lowercaseLocale;

        // Dispatch a custom event for components to react to locale changes.
        window.dispatchEvent(new CustomEvent('localechange', {
            detail: { lowercaseLocale }
        }));
    }

    /**
     * Sets the default locale.
     * @param {string} locale The locale identifier.
     */
    setDefaultLocale(locale) {
        this.#defaultLocale = locale.toLowerCase();
    }
    // #endregion ************************************************************************************/

    // #region: Private Methods **********************************************************************/
    /**
     * Gets the language attribute from the HTML tag.
     * @returns {string|null} The language attribute or null if not found.
     */
    #getHtmlLangAttribute() {
        const htmlElement = document.documentElement;

        return htmlElement ? htmlElement.getAttribute('lang') : null;
    }
    // #endregion ************************************************************************************/

}();
// #endregion ***************************************************************************************/
